import { Component, OnInit } from '@angular/core'
import { collection, getDocs, query, where, Timestamp } from '@angular/fire/firestore'
import {
    Invoice,
    InvoiceStatus,
    AdminClient,
    Parking,
    ParkingStatus,
    ParkingType,
    FirestoreStructure,
    LeadStatus,
    ContactType,
} from '@parkupp/core'
import { AdminService } from 'src/app/services/admin.service'
import { ENTERPRISE_CONFIGS, EnterpriseConfig } from 'src/app/config/enterprise.config'

export type IssueType = 'invoice' | 'payout' | 'crm' | 'parking'

export interface HygieneIssue {
    type: IssueType
    subType: string
    entity: string
    issue: string
    details: string
    ageDays: number | null
    amount: number | null
    enterpriseKey: string | null
    entityId?: string
}

@Component({
    selector: 'app-admin-data-hygiene',
    templateUrl: './admin-data-hygiene.component.html',
    styleUrls: ['./admin-data-hygiene.component.scss'],
})
export class AdminDataHygieneComponent implements OnInit {
    isLoading = true
    enterprises: EnterpriseConfig[] = Object.values(ENTERPRISE_CONFIGS)
    selectedEnterprise: string | null = null
    selectedType: IssueType | null = null

    allIssues: HygieneIssue[] = []
    filteredIssues: HygieneIssue[] = []

    counts = { invoice: 0, payout: 0, crm: 0, parking: 0 }

    constructor(private adminSvc: AdminService) {}

    ngOnInit(): void {
        this.loadAllIssues()
    }

    async loadAllIssues() {
        this.isLoading = true
        this.allIssues = []

        try {
            const [invoiceIssues, payoutIssues, crmIssues, parkingIssues] = await Promise.all([
                this.loadOverdueInvoices(),
                this.loadUnprocessedPayouts(),
                this.loadCrmIssues(),
                this.loadParkingIssues(),
            ])
            this.allIssues = [...invoiceIssues, ...payoutIssues, ...crmIssues, ...parkingIssues]
            this.applyFilters()
        } catch (error) {
            console.error('[Data Hygiene] Error loading data:', error)
        } finally {
            this.isLoading = false
        }
    }

    private toDate(value: any): Date {
        if (!value) return new Date()
        if (value instanceof Date) return value
        if (typeof value?.toDate === 'function') return value.toDate()
        if (value?.seconds) return new Date(value.seconds * 1000)
        return new Date()
    }

    private daysSince(date: Date): number {
        return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24))
    }

    async loadOverdueInvoices(): Promise<HygieneIssue[]> {
        const issues: HygieneIssue[] = []
        const invoicesRef = query(
            collection(this.adminSvc.firestore, FirestoreStructure.INVOICES),
            where('status', '==', InvoiceStatus.DUE)
        )
        const snapshot = await getDocs(invoicesRef)

        snapshot.docs.forEach((doc) => {
            const invoice = new Invoice(doc.data(), doc.id)
            const dueDate = this.toDate(invoice.dueDate)
            const daysOverdue = this.daysSince(dueDate)

            if (daysOverdue > 30) {
                const clientName = `${invoice.denormClientFirstName || ''} ${invoice.denormClientLastName || ''}`.trim() || 'Unknown'
                issues.push({
                    type: 'invoice',
                    subType: 'Overdue Invoice',
                    entity: clientName,
                    issue: `Overdue invoice ${doc.id.substring(0, 8)}...`,
                    details: `Parking: ${invoice.denormParkingTitle || 'Unknown'}, Merchant: ${invoice.denormMerchantCompanyName || 'Unknown'}`,
                    ageDays: daysOverdue,
                    amount: invoice.amountTotalDue || 0,
                    enterpriseKey: invoice.denormEnterpriseKey || null,
                    entityId: doc.id,
                })
            }
        })

        return issues
    }

    async loadUnprocessedPayouts(): Promise<HygieneIssue[]> {
        const issues: HygieneIssue[] = []
        const invoicesRef = query(
            collection(this.adminSvc.firestore, FirestoreStructure.INVOICES),
            where('status', '==', InvoiceStatus.PAID),
            where('paidOut', '==', false)
        )
        const snapshot = await getDocs(invoicesRef)

        snapshot.docs.forEach((doc) => {
            const invoice = new Invoice(doc.data(), doc.id)
            const paymentDate = this.toDate(invoice.paymentDate || invoice.createdAt)
            const daysUnpaid = this.daysSince(paymentDate)

            if (daysUnpaid > 30) {
                const payoutAmount = invoice.periodFinalPayout || (invoice.amountTotalDue || 0) * 0.85
                issues.push({
                    type: 'payout',
                    subType: 'Unprocessed Payout',
                    entity: invoice.denormMerchantCompanyName || 'Unknown',
                    issue: `Unpaid payout for invoice ${doc.id.substring(0, 8)}...`,
                    details: `Client: ${invoice.denormClientFirstName || ''} ${invoice.denormClientLastName || ''}`.trim(),
                    ageDays: daysUnpaid,
                    amount: payoutAmount,
                    enterpriseKey: invoice.denormEnterpriseKey || null,
                    entityId: doc.id,
                })
            }
        })

        return issues
    }

    async loadCrmIssues(): Promise<HygieneIssue[]> {
        const issues: HygieneIssue[] = []
        const fiveDaysAgo = Timestamp.fromDate(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000))

        const clientsRef = query(
            collection(this.adminSvc.firestore, FirestoreStructure.ADMIN_CLIENTS),
            where('createdAt', '<=', fiveDaysAgo)
        )
        const snapshot = await getDocs(clientsRef)

        snapshot.docs.forEach((doc) => {
            const client = new AdminClient(doc.data(), doc.id)
            const createdAt = this.toDate(client.createdAt)
            const daysOld = this.daysSince(createdAt)
            const name = client.fullName?.trim() || 'Unknown'
            const contactLabel = client.contactType || 'User'

            const isTerminal = client.leadStatus === LeadStatus.CONVERTED || client.leadStatus === LeadStatus.CLOSED

            // Unassigned contacts
            if (!client.userResponsible && !isTerminal) {
                issues.push({
                    type: 'crm',
                    subType: 'Unassigned Contact',
                    entity: `${name} (${contactLabel})`,
                    issue: 'Not assigned to anyone',
                    details: `Status: ${client.leadStatus || 'New'}, Email: ${client.email || 'N/A'}`,
                    ageDays: daysOld,
                    amount: null,
                    enterpriseKey: client.denormEnterpriseKey || null,
                    entityId: doc.id,
                })
            }

            // Uncontacted leads
            if ((client.leadStatus === LeadStatus.NEW || client.leadStatus === LeadStatus.CONTACT_ATTEMPTED) && !isTerminal) {
                issues.push({
                    type: 'crm',
                    subType: 'Uncontacted Lead',
                    entity: `${name} (${contactLabel})`,
                    issue: 'Not yet contacted',
                    details: `Status: ${client.leadStatus}, Email: ${client.email || 'N/A'}`,
                    ageDays: daysOld,
                    amount: null,
                    enterpriseKey: client.denormEnterpriseKey || null,
                    entityId: doc.id,
                })
            }
        })

        return issues
    }

    async loadParkingIssues(): Promise<HygieneIssue[]> {
        const issues: HygieneIssue[] = []

        const [publishedSnapshot, pendingSnapshot] = await Promise.all([
            getDocs(query(collection(this.adminSvc.firestore, FirestoreStructure.PARKINGS), where('parkingStatus', '==', ParkingStatus.PUBLISHED))),
            getDocs(query(collection(this.adminSvc.firestore, FirestoreStructure.PARKINGS), where('parkingStatus', '==', ParkingStatus.PENDING))),
        ])

        publishedSnapshot.docs.forEach((doc) => {
            const parking = new Parking(doc.data(), doc.id)
            const merchant = parking.denormMerchantCompanyName || 'Unknown'

            // Collector/Flex should not be published
            if (parking.parkingType === ParkingType.PARKING_COLLECTOR || parking.parkingType === ParkingType.FLEX) {
                issues.push({
                    type: 'parking',
                    subType: `Published ${parking.niceType}`,
                    entity: parking.title || 'Untitled',
                    issue: `${parking.niceType} parking should not be published`,
                    details: `Merchant: ${merchant}`,
                    ageDays: null,
                    amount: null,
                    enterpriseKey: parking.denormEnterpriseKey || null,
                    entityId: doc.id,
                })
            }

            // No bank details
            if (!parking.bankDetailsRef) {
                issues.push({
                    type: 'parking',
                    subType: 'No Bank Details',
                    entity: parking.title || 'Untitled',
                    issue: 'Published with no bank details',
                    details: `Merchant: ${merchant}`,
                    ageDays: null,
                    amount: null,
                    enterpriseKey: parking.denormEnterpriseKey || null,
                    entityId: doc.id,
                })
            }
        })

        pendingSnapshot.docs.forEach((doc) => {
            const parking = new Parking(doc.data(), doc.id)
            const merchant = parking.denormMerchantCompanyName || 'Unknown'
            const createdAt = this.toDate(parking.createdAt)
            const daysOld = this.daysSince(createdAt)

            issues.push({
                type: 'parking',
                subType: 'Pending Approval',
                entity: parking.title || 'Untitled',
                issue: 'Never approved or rejected',
                details: `Merchant: ${merchant}`,
                ageDays: daysOld,
                amount: null,
                enterpriseKey: parking.denormEnterpriseKey || null,
                entityId: doc.id,
            })
        })

        return issues
    }

    // Filtering

    onEnterpriseChange(key: string) {
        this.selectedEnterprise = key || null
        this.applyFilters()
    }

    onTypeFilter(type: IssueType | null) {
        this.selectedType = type
        this.applyFilters()
    }

    applyFilters() {
        let filtered = this.allIssues

        if (this.selectedEnterprise) {
            filtered = filtered.filter((i) => i.enterpriseKey === this.selectedEnterprise)
        }

        // Update counts based on enterprise filter only
        this.counts = {
            invoice: filtered.filter((i) => i.type === 'invoice').length,
            payout: filtered.filter((i) => i.type === 'payout').length,
            crm: filtered.filter((i) => i.type === 'crm').length,
            parking: filtered.filter((i) => i.type === 'parking').length,
        }

        if (this.selectedType) {
            filtered = filtered.filter((i) => i.type === this.selectedType)
        }

        this.filteredIssues = filtered.sort((a, b) => (b.ageDays || 0) - (a.ageDays || 0))
    }

    get totalIssues(): number {
        return this.counts.invoice + this.counts.payout + this.counts.crm + this.counts.parking
    }

    // Display helpers

    getTypeBadgeClass(type: IssueType): string {
        switch (type) {
            case 'invoice':
                return 'badge-invoice'
            case 'payout':
                return 'badge-payout'
            case 'crm':
                return 'badge-crm'
            case 'parking':
                return 'badge-parking'
        }
    }

    getTypeLabel(type: IssueType): string {
        switch (type) {
            case 'invoice':
                return 'Customer Invoice'
            case 'payout':
                return 'Merchant Payout'
            case 'crm':
                return 'CRM'
            case 'parking':
                return 'Parking'
        }
    }

    formatCurrency(value: number): string {
        return 'R' + value.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    }
}
